(() => {
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  function installBrandCarousels(root = document) {
    root.querySelectorAll("[data-brand-carousel]").forEach(carousel => {
      if (carousel.dataset.carouselReady === "true") return;
      carousel.dataset.carouselReady = "true";

      const track = carousel.querySelector(".agency-logo-wrapper");
      const slides = Array.from(carousel.querySelectorAll(".agency-brand-card"));
      const previous = carousel.querySelector(".agency-logo-prev");
      const next = carousel.querySelector(".agency-logo-next");
      const toggle = carousel.querySelector("[data-carousel-toggle]");
      const toggleLabel = toggle?.querySelector("[data-carousel-toggle-label]");
      const isPreview = carousel.hasAttribute("data-carousel-preview");
      const isImmediate = carousel.hasAttribute("data-carousel-immediate");
      if (!track || slides.length < 2 || !previous || !next) return;

      const requestedDelay = Number(carousel.dataset.carouselDelay);
      const autoplayDelay = Number.isFinite(requestedDelay) ? Math.max(1200, requestedDelay) : 2400;
      const pauseLabel = toggle?.dataset.pauseLabel || toggle?.getAttribute("aria-label") || "";
      const playLabel = toggle?.dataset.playLabel || pauseLabel;

      slides.forEach((slide, index) => {
        slide.dataset.swiperIndex = String(index);
      });

      let activeIndex = 0;
      let autoplayTimer = 0;
      let scrollFrame = 0;
      let settleTimer = 0;
      let resizeFrame = 0;
      let isVisible = false;
      let isInitialized = false;
      let pendingStart = isImmediate;
      let hasMoved = false;
      let trackStart = 0;
      let slideStep = 1;
      const pauses = new Set();

      const getSlides = () => Array.from(track.querySelectorAll(".agency-brand-card"));

      const measure = () => {
        const orderedSlides = getSlides();
        const trackLeft = track.offsetLeft;
        trackStart = orderedSlides[0].offsetLeft - trackLeft;
        slideStep = Math.max(orderedSlides[1].offsetLeft - orderedSlides[0].offsetLeft, 1);
      };

      const getVisualIndex = () => Math.max(0, Math.min(
        slides.length - 1,
        Math.round((track.scrollLeft - trackStart) / slideStep)
      ));

      const getActiveSlide = () => getSlides()[getVisualIndex()];

      const setProgress = slide => {
        activeIndex = Number(slide?.dataset.swiperIndex || 0);
        if (isPreview && carousel.dataset.previewIndex !== String(activeIndex)) carousel.dataset.previewIndex = String(activeIndex);
        carousel.style.setProperty("--vc-carousel-progress", `${((activeIndex + 1) / slides.length) * 100}%`);
      };

      const reorder = (moveSlide, scrollDelta) => {
        const currentScroll = track.scrollLeft;
        track.style.scrollBehavior = "auto";
        track.style.scrollSnapType = "none";
        moveSlide();
        measure();
        track.scrollLeft = Math.max(0, currentScroll + scrollDelta);
        track.getBoundingClientRect();
        track.style.removeProperty("scroll-behavior");
        track.style.removeProperty("scroll-snap-type");
      };

      const rotateLastToStart = () => {
        const last = track.lastElementChild;
        reorder(() => track.prepend(last), slideStep);
      };

      const rotateFirstToEnd = () => {
        const first = track.firstElementChild;
        reorder(() => track.append(first), -slideStep);
      };

      const update = () => {
        if (!isInitialized) return;
        setProgress(getActiveSlide());
      };

      const prepareEdges = () => {
        measure();
        let visualIndex = getVisualIndex();
        if (visualIndex === 0) {
          rotateLastToStart();
          visualIndex = 1;
        }

        let maxScroll = track.scrollWidth - track.clientWidth;
        while (visualIndex > 1 && trackStart + ((visualIndex + 1) * slideStep) > maxScroll + 1) {
          rotateFirstToEnd();
          visualIndex -= 1;
          maxScroll = track.scrollWidth - track.clientWidth;
        }
        update();
      };

      const settle = () => {
        window.clearTimeout(settleTimer);
        if (!isInitialized || !isVisible) return;
        prepareEdges();
      };

      const activate = () => {
        if (isInitialized) return;
        isInitialized = true;
        carousel.dataset.carouselActive = "true";
        measure();
        update();

        if ("ResizeObserver" in window) {
          const resizeObserver = new ResizeObserver(() => {
            if (!isVisible) return;
            window.cancelAnimationFrame(resizeFrame);
            resizeFrame = window.requestAnimationFrame(() => {
              if (!isVisible) return;
              if (hasMoved) prepareEdges();
              else {
                measure();
                update();
              }
            });
          });
          resizeObserver.observe(track);
        }
      };

      const scheduleSettle = () => {
        window.clearTimeout(settleTimer);
        settleTimer = window.setTimeout(settle, reducedMotion.matches ? 0 : 720);
      };

      const goBy = delta => {
        pendingStart = false;
        hasMoved = true;
        activate();
        track.scrollTo({ left: track.scrollLeft, behavior: "auto" });
        prepareEdges();
        const currentSlide = getActiveSlide();
        const targetSlide = delta > 0 ? currentSlide.nextElementSibling : currentSlide.previousElementSibling;
        if (!targetSlide) return;

        setProgress(targetSlide);
        track.scrollTo({
          left: targetSlide.offsetLeft - track.offsetLeft,
          behavior: reducedMotion.matches ? "auto" : "smooth"
        });
        scheduleSettle();
      };

      const stopAutoplay = () => {
        window.clearInterval(autoplayTimer);
        if (isPreview) carousel.dataset.previewRunning = "false";
      };
      const startAutoplay = () => {
        stopAutoplay();
        if (reducedMotion.matches || !isVisible || pauses.size || document.hidden) return;
        if (isPreview) {
          carousel.dataset.previewRunning = "true";
          return;
        }
        if (pendingStart) goBy(1);
        autoplayTimer = autoplayDelay === 2400
          ? window.setInterval(() => goBy(1), 2400)
          : window.setInterval(() => goBy(1), autoplayDelay);
      };
      const pause = reason => {
        pauses.add(reason);
        stopAutoplay();
      };
      const resume = reason => {
        pauses.delete(reason);
        startAutoplay();
      };
      const updateToggle = () => {
        if (!toggle) return;
        const manuallyPaused = pauses.has("manual");
        const label = manuallyPaused ? playLabel : pauseLabel;
        toggle.setAttribute("aria-pressed", String(manuallyPaused));
        if (label) toggle.setAttribute("aria-label", label);
        if (toggleLabel && label) toggleLabel.textContent = label;
        toggle.hidden = reducedMotion.matches;
        toggle.disabled = reducedMotion.matches;
      };

      previous.addEventListener("click", () => {
        goBy(-1);
        startAutoplay();
      });
      next.addEventListener("click", () => {
        goBy(1);
        startAutoplay();
      });
      carousel.addEventListener("keydown", event => {
        if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
        event.preventDefault();
        goBy(event.key === "ArrowRight" ? 1 : -1);
        startAutoplay();
      });
      track.addEventListener("scroll", () => {
        hasMoved = true;
        window.cancelAnimationFrame(scrollFrame);
        scrollFrame = window.requestAnimationFrame(update);
        if (!("onscrollend" in track)) scheduleSettle();
      }, { passive: true });
      if ("onscrollend" in track) track.addEventListener("scrollend", settle);
      carousel.addEventListener("pointerenter", event => {
        if (event.pointerType !== "mouse") return;
        pauses.add("hover");
        stopAutoplay();
      });
      carousel.addEventListener("pointerleave", event => {
        if (event.pointerType !== "mouse") return;
        resume("hover");
      });
      track.addEventListener("pointerdown", () => {
        pendingStart = false;
        hasMoved = true;
        activate();
        pause("swipe");
        track.classList.add("is-swiping");
      });
      track.addEventListener("pointerup", () => {
        resume("swipe");
        track.classList.remove("is-swiping");
        scheduleSettle();
      });
      track.addEventListener("pointercancel", () => {
        resume("swipe");
        track.classList.remove("is-swiping");
      });
      carousel.addEventListener("focusin", event => {
        if ((!isPreview && !isImmediate) || event.target?.matches(":focus-visible")) pause("focus");
      });
      carousel.addEventListener("focusout", event => {
        if (event.relatedTarget && carousel.contains(event.relatedTarget)) return;
        resume("focus");
      });
      toggle?.addEventListener("click", () => {
        if (pauses.has("manual")) {
          if (isPreview || (isImmediate && !toggle.matches(":focus-visible"))) pauses.delete("focus");
          resume("manual");
        }
        else pause("manual");
        updateToggle();
      });
      document.addEventListener("visibilitychange", startAutoplay);
      reducedMotion.addEventListener("change", () => {
        updateToggle();
        startAutoplay();
      });
      updateToggle();

      if ("IntersectionObserver" in window) {
        const observer = new IntersectionObserver(entries => {
          isVisible = entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.15);
          if (isVisible) activate();
          startAutoplay();
        }, { threshold: 0.15 });
        observer.observe(carousel);
      } else {
        isVisible = true;
        startAutoplay();
      }
    });
  }

  function installCounters(root = document) {
    root.querySelectorAll(".agency-home-proofline").forEach(proofline => {
      if (proofline.dataset.countReady === "true") return;
      proofline.dataset.countReady = "true";
      const counters = Array.from(proofline.querySelectorAll("[data-count-up]"));

      const run = () => counters.forEach(counter => {
        if (counter.dataset.counted === "true") return;
        counter.dataset.counted = "true";
        const finalText = counter.textContent.trim();
        const rawNumber = finalText.replace(/[^0-9]/g, "");
        if (!rawNumber) return;
        counter.setAttribute("aria-label", finalText);
        if (reducedMotion.matches) return;
        const textNode = counter.firstChild;
        if (!textNode || textNode.nodeType !== 3 || textNode.nextSibling) return;

        const target = Number(rawNumber);
        const suffix = finalText.includes("+") ? "+" : "";
        const formatter = new Intl.NumberFormat("en-US");
        const startedAt = performance.now();
        textNode.nodeValue = `0${suffix}`;
        const tick = now => {
          const progress = Math.min((now - startedAt) / 1300, 1);
          textNode.nodeValue = `${formatter.format(Math.round(target * (1 - Math.pow(1 - progress, 3))))}${suffix}`;
          if (progress < 1) window.setTimeout(() => tick(performance.now()), 80);
          else textNode.nodeValue = finalText;
        };
        tick(startedAt);
      });

      if (reducedMotion.matches || !("IntersectionObserver" in window)) {
        run();
        return;
      }
      const observer = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        observer.disconnect();
        run();
      }, { threshold: 0.25, rootMargin: "0px 0px -8% 0px" });
      observer.observe(proofline);
    });
  }

  const initialize = (root = document) => {
    installBrandCarousels(root);
    installCounters(root);
  };

  initialize();
  document.addEventListener("shopify:section:load", event => initialize(event.target));
})();
